import React from 'react';
import { StyleSheet } from 'react-native';

import Placeholder from '@components/Placeholders';
import ScreenContainer from '@components/ScreenContainer';
import { Route, TabView } from '@components/TabView';
import { LargeUserCardList } from '@components/User/CardList';
import { useFollowerUsersQuery, useFollowingUsersQuery } from '@db/Follows/Queries';
import { createAppNavigation } from '@navigation/AppNavigation';
import {
  ViewFollowsScreenToggleState as ToggleState,
  ViewFollowsScreenProps,
} from '@navigation/Types';

export default function ViewFollowsScreen({ navigation, route }: ViewFollowsScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const userId = route.params?.userId;
  const [toggleState, setToggleState] = React.useState<ToggleState>(ToggleState['FOLLOWERS']);
  const followersQuery = useFollowerUsersQuery({ userId });
  const followingQuery = useFollowingUsersQuery({ userId });
  const loading = followersQuery.loading || followingQuery.loading;
  const routeToggleState = route.params?.toggleState;

  React.useEffect(() => {
    if (route.params?.userDisplayName) {
      navigation.setOptions({
        headerTitle: route.params?.userDisplayName,
      });
    }
  }, [navigation, route.params?.userDisplayName]);

  React.useEffect(() => {
    if (routeToggleState) {
      // Route params can request a tab after this screen is already mounted.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setToggleState(routeToggleState);
      navigation.setParams({ toggleState: undefined });
    }
  }, [navigation, routeToggleState]);

  const EmptyComponent = (
    <Placeholder
      icon="account"
      title={'No ' + (toggleState === ToggleState.FOLLOWERS ? 'followers' : 'following')}
    />
  );

  const itemProps: React.ComponentProps<typeof LargeUserCardList>['itemProps'] = {
    onPress: (user) => appNavigation.openProfile(user.userId),
    messageButton: {
      onPress: appNavigation.openChat,
    },
  };

  const Followers = (
    <LargeUserCardList
      data={followersQuery.users}
      itemProps={itemProps}
      ListEmptyComponent={EmptyComponent}
      contentContainerStyle={styles.listContent}
    />
  );

  const Following = (
    <LargeUserCardList
      data={followingQuery.users}
      itemProps={itemProps}
      ListEmptyComponent={EmptyComponent}
      contentContainerStyle={styles.listContent}
    />
  );

  const routes: Route[] = [
    { key: ToggleState.FOLLOWERS, title: 'Followers' },
    { key: ToggleState.FOLLOWING, title: 'Following' },
  ];

  const renderScene = ({ route }: { route: Route }) => {
    switch (route.key) {
      case ToggleState.FOLLOWERS:
        return Followers;
      case ToggleState.FOLLOWING:
        return Following;
      default:
        return null;
    }
  };

  return (
    <ScreenContainer loading={loading} style={styles.background}>
      <TabView
        routes={routes}
        renderScene={renderScene}
        initiallyTrackRouteKey={toggleState}
        onIndexChange={(index) => setToggleState(routes[index].key as ToggleState)}
      />
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  background: {
    alignItems: 'stretch',
  },
  listContent: {
    marginTop: 5,
  },
});
